import api from './apiService';

export interface SportCategory {
  id?: number;
  slug?: string;
  name: string;
  description?: string;
  icon?: string;
  is_active?: boolean;
}

export interface ArenaDetail {
  id?: number;
  slug?: string;
  name?: string;
  email?: string;
  phone?: string;
  description?: string;
  address?: string;
  kode_wilayah?: string;
  city?: string;
  kecamatan?: string;
  sport_categories?: { categories?: string[] };
  maps?: {
    google_maps_url?: string;
    city?: string;
    kecamatan?: string;
    address?: string;
    city_slug?: string;
  };
  social_links?: {
    instagram?: string;
    whatsapp?: string;
    tiktok?: string;
    youtube?: string;
  };
  owner: {
    name?: string;
    email?: string;
    phone?: string;
    position?: string;
  };
  staff?: {
    name?: string;
    email?: string;
    phone?: string;
    position?: string;
  };
  payment_account?: {
    name: string;
    bank: string;
    number: string;
  };
}

export interface UpdateArenaPayload {
  name: string;
  slug?: string;
  kode_wilayah?: string;
  sport_categories?: string[];
  address?: string;
  kecamatan?: string;
  city?: string;
  city_slug?: string;
  google_maps_url?: string;
  description?: string;
  social_links?: {
    instagram?: string;
    whatsapp?: string;
    tiktok?: string;
    youtube?: string;
  };
}

export interface UpdateArenaResponse {
  success: boolean;
  message: string;
  data?: any;
}

export interface SuperAdminArena {
  id: number;
  slug: string;
  name: string;
  email: string;
  phone: string;

  owner_id?: number;
  staff_id?: number;

  sport_categories?: {
    categories?: string[];
  };

  is_verified: boolean;
  is_active: boolean;
  is_hidden: boolean;

  created_at: string;
  updated_at?: string;
  expired_at?: string | null;
}

export interface GetSuperAdminArenasResponse {
  items: SuperAdminArena[];
  total: number;
}

class ArenaService {
  async getSportCategories(page?: string): Promise<SportCategory[]> {
    try {
      let objPage = '';
      if (page == 'undefined' || page == undefined || page == null || page == '') {
        objPage = `&object=home`;
      }
      const res = await api.get(`/sport-categories?unlimited=true${objPage}`);
      if (Array.isArray(res.data)) return res.data;
      if (res.data?.data?.sport_categories) return res.data.data.sport_categories;
      if (res.data?.data?.categories) return res.data.data.categories;
      if (res.data?.sport_categories) return res.data.sport_categories;
      if (res.data?.categories) return res.data.categories;
      return [];
    } catch (error) {
      console.error('Error fetching sport categories', error);
      return [];
    }
  }

  async updateArena(slug: string, payload: UpdateArenaPayload): Promise<UpdateArenaResponse> {
    try {
      const res = await api.put(`/arenas/slug/${slug}`, payload);

      return res.data;
    } catch (error: any) {
      console.error('Error updating arena', error);
      if (error.response?.data) return error.response.data;
      return { success: false, message: 'Network error' };
    }
  }

  async getArenaBySlug(slug: string): Promise<ArenaDetail | null> {
    try {
      const res = await api.get(`/arenas/slug/${slug}`);
      if (res.data?.data?.arena) return res.data.data.arena;
      if (res.data?.arena) return res.data.arena;
      return null;
    } catch (error) {
      console.error('Error fetching arena by slug', error);
      return null;
    }
  }

  async updateArenaStaff(
    slug: string,
    payload: { name?: string; email?: string; phone?: string }
  ): Promise<UpdateArenaResponse> {
    try {
      const res = await api.put(`/arenas/slug/${slug}/staff`, payload);

      return res.data;
    } catch (error: any) {
      console.error('Error updating arena staff', error);
      if (error.response?.data) return error.response.data;
      return { success: false, message: 'Network error' };
    }
  }

  async updateArenaOperational(slug: string, formData: FormData): Promise<UpdateArenaResponse> {
    try {
      const res = await api.put(`/arenas/slug/${slug}/operational`, formData);

      return res.data;
    } catch (error: any) {
      console.error('Error updating arena operational', error);
      if (error.response?.data) return error.response.data;
      return { success: false, message: 'Network error' };
    }
  }

  async updateSportoArena(
    slug: string,
    payload: {
      email?: string;
      current_password?: string;
      new_password?: string;
      confirm_new_password?: string;
      name: string;
      bank: string;
      account_number: string;
    }
  ): Promise<UpdateArenaResponse> {
    try {
      const res = await api.put(`/arenas/slug/${slug}/account`, payload);

      return res.data;
    } catch (error: any) {
      console.error('Error updating sporto arena', error);
      if (error.response?.data) return error.response.data;
      return { success: false, message: 'Network error' };
    }
  }

  async getSuperAdminArenas(
    page = 1,
    limit = 10,
    search?: string,
    active?: string
  ): Promise<GetSuperAdminArenasResponse> {
    try {
      const params = new URLSearchParams();

      params.append('page', String(page));
      params.append('limit', String(limit));

      if (search) {
        params.append('search', search);
      }

      if (active !== undefined && active !== '') {
        params.append('active', active);
      }

      const res = await api.get(`/superadmin/arenas?${params.toString()}`);

      return {
        items: res.data?.data?.items || [],
        total: res.data?.data?.total || 0,
      };
    } catch (error) {
      console.error('Error fetching superadmin arenas', error);

      return {
        items: [],
        total: 0,
      };
    }
  }

  async updateArenaVerification(
    slug: string,
    isVerified: boolean
  ): Promise<{
    success: boolean;
    message: string;
    data?: any;
  }> {
    try {
      const res = await api.put(`/superadmin/arenas/${slug}/verification`, {
        is_verified: isVerified,
      });

      return res.data;
    } catch (error: any) {
      return (
        error.response?.data || {
          success: false,
          message: 'Terjadi kesalahan jaringan',
        }
      );
    }
  }

  async updateArenaActiveStatus(
    slug: string,
    isActive: boolean
  ): Promise<{
    success: boolean;
    message: string;
    data?: any;
  }> {
    try {
      const res = await api.put(`/superadmin/arenas/${slug}/active`, {
        is_active: isActive,
      });

      return res.data;
    } catch (error: any) {
      return (
        error.response?.data || {
          success: false,
          message: 'Terjadi kesalahan jaringan',
        }
      );
    }
  }

  async getSuperAdminTagihanSporto(
    slug: string
  ): Promise<{ dun_limit: number; dun_terpakai: number } | null> {
    try {
      const res = await api.get(`/superadmin/arenas/${slug}/tagihan-sporto`);
      return res.data?.data || null;
    } catch {
      return null;
    }
  }

  async updateSuperAdminTagihanSporto(
    slug: string,
    dunLimit: number
  ): Promise<{ success: boolean; message?: string }> {
    try {
      const res = await api.put(`/superadmin/arenas/${slug}/tagihan-sporto`, {
        dun_limit: dunLimit,
      });
      return { success: res.data?.success ?? true, message: res.data?.message };
    } catch (error: any) {
      if (error.response?.data) return error.response.data;
      return { success: false, message: 'Network error' };
    }
  }
}

export const arenaService = new ArenaService();
